import { ConfigurationStatus, SystemConnection } from '@Types';
import {
	ConfigurationDataSourceType,
	ConfigurationRow,
} from '@Types/configurations.types';
import { SensorsUptimeDataRequest } from '@Types/edgeAnalytics.types';

export const parsePercentageValueToStatus = (
	value: number,
): ConfigurationStatus => {
	let status = 'Poor';
	if (value >= 0.95) status = 'Good';
	else if (value >= 0.85) status = 'Fair';

	return status as ConfigurationStatus;
};

const mapCollectionInterval = (currentInterval: string | number): number => {
	let newInterval = parseInt(currentInterval ? `${currentInterval}` : '300');
	if (newInterval < 60) newInterval = 60;

	return newInterval;
};

/**
 * Purpose: include DataConnector's inherent query lag which is randomly distributed over 1 collection interval (limited to 1hr). Taking 1/2 as avg
 * @param collectionInterval
 * @returns intervalLagMinutes
 */
const getIntervalLagMin = (collectionInterval: number) => {
	const maximunCollectionInterval = 3600; // 1 Hour
	let collectionIntervalLag = 0;
	if (collectionInterval >= maximunCollectionInterval) {
		collectionIntervalLag = Math.ceil(maximunCollectionInterval / 2 / 60);
	} else {
		collectionIntervalLag = Math.ceil(collectionInterval / 2 / 60);
	}
	return collectionIntervalLag;
};

export const mapUptimeDataRequest = (
	systemConnections: SystemConnection[],
	sensorConfigurations: ConfigurationRow[],
	unloadTimeLagMin = 0,
): SensorsUptimeDataRequest[] => {
	const connectionsMap = new Map(
		systemConnections.map(connection => [
			connection.SystemConnectionId,
			connection,
		]),
	);

	return sensorConfigurations.map(configuration => {
		const {
			sensorId,
			collectionInterval: confInterval,
			dataSourceId = '',
		} = configuration;
		const connection = connectionsMap.get(dataSourceId);

		const systemConnectionInterval = connection
			? connection.sampleDurationSeconds
			: 0;
		const collectionInterval = mapCollectionInterval(
			confInterval || systemConnectionInterval,
		);
		const collectionIntervalLagMin = getIntervalLagMin(collectionInterval);
		const connectionLagMin = connection ? connection.queryLagMinutes : 0;

		return {
			sensorId,
			collectionInterval,
			timeLagMin:
				connectionLagMin + collectionIntervalLagMin + unloadTimeLagMin,
		};
	});
};

export const filterByAllowedHealthStatusDataSource = (
	sensors: ConfigurationRow[],
) => {
	const validHealthStatusSources: Array<string> = [
		ConfigurationDataSourceType.ODBC,
		ConfigurationDataSourceType.OLEDB,
		ConfigurationDataSourceType.OPCUA,
		'', // undefined/empty dataSourceType should be treated as ODBC
	];
	return sensors.filter(sensor =>
		validHealthStatusSources.includes(
			sensor.dataSourceType || sensor.sourceType || '',
		),
	);
};

export const mapLatestMeasureToFixed = (latestMeasure?: number): string => {
	if (latestMeasure === undefined || latestMeasure === null) return '';

	return latestMeasure.toFixed(3);
};
